var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { getMockProductByKey, mockProducts } from "@/api/mock/mockData";
const STORAGE_KEY = "mockCart";
const PROMO_CODE = "PROMO20";
const PROMO_ID = "promo20-id";
const createPrice = (centAmount) => ({
    type: "centPrecision",
    currencyCode: "EUR",
    centAmount,
    fractionDigits: 2,
});
const toCart = (cart) => cart;
const makeEmptyCart = () => ({
    id: `mock-cart-${Date.now()}`,
    version: 1,
    lineItems: [],
    discountCodes: [],
    totalPrice: createPrice(0),
});
const getVariantData = (productId, variantId) => {
    var _a;
    const product = mockProducts.find((p) => p.id === productId);
    const variant = (_a = product === null || product === void 0 ? void 0 : product.variants) === null || _a === void 0 ? void 0 : _a.find((v) => v.id === variantId);
    return {
        key: (variant === null || variant === void 0 ? void 0 : variant.key) || String(variantId),
        images: (variant === null || variant === void 0 ? void 0 : variant.images) || (product === null || product === void 0 ? void 0 : product.images) || [],
    };
};
const getProductData = (productId) => {
    const byId = mockProducts.find((p) => p.id === productId);
    if (byId)
        return byId;
    return getMockProductByKey(productId);
};
export class FakeCartService {
    readCart() {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw)
            return null;
        try {
            return JSON.parse(raw);
        }
        catch (_a) {
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }
    }
    writeCart(cart) {
        const normalized = this.recalculateTotals(cart);
        localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    }
    recalculateTotals(cart) {
        const subtotal = cart.lineItems.reduce((sum, item) => {
            var _a, _b, _c, _d, _e;
            const perItem = (_e = (_d = (_c = (_b = (_a = item.discountedPricePerQuantity) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.discountedPrice) === null || _c === void 0 ? void 0 : _c.value) === null || _d === void 0 ? void 0 : _d.centAmount) !== null && _e !== void 0 ? _e : item.price.value.centAmount;
            return sum + perItem * item.quantity;
        }, 0);
        const hasPromo = cart.discountCodes.some((d) => d.discountCode.id === PROMO_ID);
        const totalCentAmount = hasPromo ? Math.round(subtotal * 0.8) : subtotal;
        return Object.assign(Object.assign({}, cart), { totalPrice: createPrice(totalCentAmount) });
    }
    getActiveCart() {
        return __awaiter(this, void 0, void 0, function* () {
            const cart = this.readCart();
            if (!cart) {
                throw new Error("No active cart found");
            }
            return toCart(this.recalculateTotals(cart));
        });
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    createCart(_customer) {
        return __awaiter(this, void 0, void 0, function* () {
            const cart = makeEmptyCart();
            this.writeCart(cart);
            return toCart(cart);
        });
    }
    updateCart(cartId, payload) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const cart = this.readCart();
            if (!cart || cart.id !== cartId) {
                throw new Error("Cart not found");
            }
            const nextCart = Object.assign(Object.assign({}, cart), { version: cart.version + 1, lineItems: [...cart.lineItems], discountCodes: [...cart.discountCodes] });
            const actions = (payload.actions || []);
            for (const action of actions) {
                if (action.action === "addLineItem" && action.productId) {
                    const product = getProductData(action.productId);
                    if (!product)
                        continue;
                    const variantId = action.variantId || 1;
                    const existing = nextCart.lineItems.find((li) => li.productId === action.productId && li.variant.id === variantId);
                    if (existing) {
                        existing.quantity += action.quantity || 1;
                        continue;
                    }
                    const centAmount = Math.round((product.priceDiscounted || product.price) * 100);
                    const baseCentAmount = Math.round(product.price * 100);
                    const variantData = getVariantData(action.productId, variantId);
                    const lineItem = {
                        id: `li-${Date.now()}-${Math.random().toString(16).slice(2)}`,
                        productId: product.id,
                        name: { "en-US": product.name },
                        price: { value: createPrice(baseCentAmount) },
                        quantity: action.quantity || 1,
                        variant: {
                            id: variantId,
                            key: variantData.key,
                            images: variantData.images,
                        },
                    };
                    if (product.priceDiscounted > 0) {
                        lineItem.discountedPricePerQuantity = [
                            { discountedPrice: { value: createPrice(centAmount) } },
                        ];
                    }
                    nextCart.lineItems.push(lineItem);
                }
                if (action.action === "removeLineItem" && action.lineItemId) {
                    nextCart.lineItems = nextCart.lineItems.filter((li) => li.id !== action.lineItemId);
                }
                if (action.action === "changeLineItemQuantity" && action.lineItemId) {
                    nextCart.lineItems = nextCart.lineItems
                        .map((li) => li.id === action.lineItemId
                        ? Object.assign(Object.assign({}, li), { quantity: Math.max(1, action.quantity || 1) }) : li)
                        .filter((li) => li.quantity > 0);
                }
                if (action.action === "addDiscountCode" && ((_a = action.code) === null || _a === void 0 ? void 0 : _a.toUpperCase()) === PROMO_CODE) {
                    const exists = nextCart.discountCodes.some((d) => d.discountCode.id === PROMO_ID);
                    if (!exists) {
                        nextCart.discountCodes.push({
                            discountCode: {
                                id: PROMO_ID,
                                typeId: "discount-code",
                            },
                        });
                    }
                }
                if (action.action === "removeDiscountCode") {
                    const discountCodeId = (_b = action.discountCode) === null || _b === void 0 ? void 0 : _b.id;
                    nextCart.discountCodes = nextCart.discountCodes.filter((d) => d.discountCode.id !== discountCodeId);
                }
            }
            this.writeCart(nextCart);
            return toCart(this.recalculateTotals(nextCart));
        });
    }
    changeLineItemQuantity(cartId, version, lineItemId, quantity) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.updateCart(cartId, {
                version,
                actions: [
                    {
                        action: "changeLineItemQuantity",
                        lineItemId,
                        quantity,
                    },
                ],
            });
        });
    }
    addDiscountCode(cartId, version, code) {
        return __awaiter(this, void 0, void 0, function* () {
            if (code.toUpperCase() !== PROMO_CODE) {
                throw new Error("Invalid promo code");
            }
            return this.updateCart(cartId, {
                version,
                actions: [
                    {
                        action: "addDiscountCode",
                        code,
                    },
                ],
            });
        });
    }
    removeDiscountCode(cartId, version, discountCodeId) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.updateCart(cartId, {
                version,
                actions: [
                    {
                        action: "removeDiscountCode",
                        discountCode: {
                            typeId: "discount-code",
                            id: discountCodeId,
                        },
                    },
                ],
            });
        });
    }
}
